import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Row, Col, Button, Alert, Clearfix } from 'react-bootstrap';
import { uniq as _uniq, includes as _includes, find as _find, findIndex as _findIndex } from 'lodash';
import moment from 'moment';
import 'moment-timezone';

import EntityPropsUpdater from 'component/EntityPropsUpdater';

import FormInput from 'components/forms/FormInput';
import FormAutocomplete from 'components/forms/FormAutocomplete';
import FormTags from 'components/forms/FormTags';
import FormSwitchery from 'components/forms/FormSwitchery';
import FormRichEditor from 'components/forms/FormRichEditor';

import SocialMediaLoginSecret from 'components/modals/SocialMediaLoginSecret';

import List from 'components/List';

import may from 'js/security';

const tzOptions = moment.tz.names().map(timezone => {
  return { value: timezone, label: timezone };
});

class WhiteLabelData extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      showEnterSocialMediaSecret: false,
      socialMediaLoginSecretType: '',
      brands: [],
    };
  }

  componentDidUpdate = prevProps => {
    const { brands } = this.props;

    if (prevProps.brands.pending && !brands.pending) {
      // brands list is loaded, we may update the state
      this.setState({ brands: brands.data.list });
    }
  };

  showEnterSocialMediaSecret = type => {
    this.setState({ showEnterSocialMediaSecret: true, socialMediaLoginSecretType: type });
  };

  closeEnterSocialMediaSecret = () => {
    this.setState({ showEnterSocialMediaSecret: false });
  };

  isWhiteLabelLanguageChecked = language => {
    return !!_includes(this.props.entity.getLanguages(), language);
  };

  whiteLabelLanguageChange = e => {
    const { target } = e;
    const language = target.value;
    const { checked } = target;

    const { entity, changeEntity } = this.props;
    let newValue;
    if (checked) {
      newValue = entity.getLanguages().slice();
      newValue.push(language);
    } else {
      newValue = entity.getLanguages().filter(r => {
        return r !== language;
      });
    }

    const newEntity = { ...entity.updateValue('languages', newValue) };

    changeEntity(newEntity);
  };

  addCurrency = (fieldName, currency) => {
    const { entity, changeEntity } = this.props;

    const newValue = entity.getCurrencies().slice();
    newValue.push(currency);

    const newEntity = { ...entity.updateValue('currencies', _uniq(newValue)) };

    changeEntity(newEntity);
  };

  removeCurrency = i => {
    const { entity, changeEntity } = this.props;
    const newValue = entity.getCurrencies().slice();
    newValue.splice(i, 1);

    const newEntity = { ...entity.updateValue('currencies', newValue) };

    changeEntity(newEntity);
  };

  addBrand = (fieldName, brand) => {
    const { entity } = this.props;
    let brands = entity.getBrands().slice();
    const lengthBefore = brands.length;

    brands.push(brand.id);
    brands = _uniq(brands);

    if (brands.length > lengthBefore) {
      // a brand was added
      this.updateEntity('brands', brands);

      this.setState(prevState => {
        const stateBrands = prevState.brands.slice();
        stateBrands.push(brand);

        return { brands: stateBrands };
      });
    }
  };

  removeBrand = id => {
    const { entity } = this.props;
    const brands = entity.getBrands().slice();
    brands.splice(brands.indexOf(id), 1);
    this.updateEntity('brands', brands);

    this.setState(prevState => {
      const stateBrands = prevState.brands.slice();
      stateBrands.splice(_findIndex(stateBrands, ['id', id]), 1);

      return { brands: stateBrands };
    });
  };

  getSocialMediaLoginIcon = type => {
    const { socialMediaLoginSecrets } = this.props;

    if (socialMediaLoginSecrets.pending) {
      return <i className="fa fa-circle-notch fa-spin fa-fw" />;
    }

    // check if the secret was already saved
    const socialMediaLoginSecret = _find(socialMediaLoginSecrets.data.list, ['type', type]);
    if (socialMediaLoginSecret && socialMediaLoginSecret.secret) {
      return <i className="fas fa-check text-success hint-text fa-fw" />;
    }

    // default: we are still missing the app secret
    return <i className="fas fa-times text-warning hint-text fa-fw" />;
  };

  updateSocialMediaEntity = (name, value) => {
    this.updateEntity(name, value, ['socialMedia']);
  };

  render() {
    const {
      intl: { messages },
      entity,
      entityLoaded,
      user,
      disabled,
      inModal,
      languages,
      socialMediaLoginSecrets,
      brands,
    } = this.props;
    const { brands: stateBrands } = this.state;

    const isAdmin = may('ROLE_ADMIN', user.roles);
    const isSuperadmin = may('ROLE_SUPERADMIN', user.roles);

    return (
      <div className="clearfix">
        {entityLoaded && (
          <div>
            <List noRender listName="languages" url="/api/languages.json" list={languages} />

            {!!entity.getId() && isAdmin && (
              <List
                noRender
                listName="socialMediaLoginSecrets"
                url={`/api/whitelabels/${entity.getId()}/socialmedialoginsecrets.json`}
                list={socialMediaLoginSecrets}
              />
            )}
            {!!entity.getId() && (
              <List
                noRender
                listName="whiteLabelBrands"
                url={`/api/whitelabels/${entity.getId()}/brands.json`}
                list={brands}
              />
            )}

            <Row>
              <Col xs={12} md={inModal ? 12 : 4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p className="form-group-title">{messages.formGroup.basic_information}</p>
                    <div className="form-group-attached">
                      <FormInput
                        label={messages.name}
                        name="name"
                        value={entity.getName()}
                        meta={entity.getNameMetaInformation()}
                        validation={entity.getNameValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                      <FormTags
                        label={messages.domain}
                        name="domains"
                        value={entity.getDomains()}
                        meta={entity.getDomainsMetaInformation()}
                        validation={entity.getDomainsValidationResult()}
                        onChange={this.updateEntity}
                        messages={messages}
                        disabled={disabled}
                      />
                      <FormInput
                        label={messages.email + ' ' + messages.domain}
                        name="mailDomain"
                        value={entity.getMailDomain()}
                        meta={entity.getMailDomainMetaInformation()}
                        validation={entity.getMailDomainValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                    </div>

                    <div className="m-t-15 form-group-attached">
                      <FormAutocomplete
                        label={messages.timezone}
                        name="timezone"
                        value={entity.getDefaultTimezone()}
                        meta={entity.getDefaultTimezoneMetaInformation()}
                        validation={entity.getDefaultTimezoneValidationResult()}
                        onChange={this.updateEntity}
                        options={tzOptions}
                        complete
                        disabled={disabled}
                      />
                    </div>

                    <p className="m-t-15 form-group-title">
                      {messages.languages}
                      {languages.pending && <i className="m-l-10 fa fa-circle-notch fa-spin" />}
                    </p>
                    <div className="form-group-attached">
                      <FormAutocomplete
                        label={messages.primary_language}
                        name="mainLanguage"
                        value={entity.getMainLanguage()}
                        meta={entity.getMainLanguageMetaInformation()}
                        validation={entity.getMainLanguageValidationResult()}
                        onChange={this.updateEntity}
                        options={languages.data.list.map(language => {
                          return { value: language.id, label: language.name };
                        })}
                        complete
                        disabled={disabled}
                      />
                    </div>

                    <div>
                      {languages.data.list.map((language, i) => {
                        return (
                          <div className="checkbox check-success" key={i}>
                            <input
                              disabled={disabled}
                              id={'whiteLabelLanguage' + i}
                              name="languages[]"
                              type="checkbox"
                              value={language.id}
                              checked={this.isWhiteLabelLanguageChecked(language.id)}
                              onChange={this.whiteLabelLanguageChange}
                            />
                            <label for={'whiteLabelLanguage' + i}>{language.name}</label>
                          </div>
                        );
                      })}
                    </div>

                    <p className="m-t-15 form-group-title">{messages.currencies}</p>
                    <div className="form-group-attached">
                      <FormAutocomplete
                        label={messages.currency}
                        name="currency"
                        onChange={this.addCurrency}
                        meta={entity.getCurrenciesMetaInformation()}
                        autocompleteType="currency"
                        complete
                        disabled={disabled}
                      />

                      {entity.getCurrencies().map((currency, i) => {
                        return (
                          <div key={currency} className="form-group form-group-default">
                            <Clearfix>
                              <div className="pull-left">{currency}</div>
                              {isAdmin && (
                                <div className="pull-right">
                                  <Button
                                    className="text-danger pull-right"
                                    bsSize="xsmall"
                                    onClick={() => this.removeCurrency(i)}
                                    disabled={disabled}
                                  >
                                    <i className="fa fa-times" />
                                  </Button>
                                </div>
                              )}
                            </Clearfix>
                          </div>
                        );
                      })}
                    </div>

                    <p className="m-t-15">{'Standard '+messages.vat}</p>
                    <div className="form-group-attached">
                      <FormInput
                        label={messages.vat}
                        name="defaultVAT"
                        type="number"
                        min={1}
                        step={0.01}
                        value={entity.getDefaultVAT()}
                        meta={entity.getDefaultVATMetaInformation()}
                        validation={entity.getDefaultVATValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                    </div>

                    <p className="m-t-15 form-group-title">{messages.brands}</p>
                    <div className="form-group-attached">
                      <FormAutocomplete
                        label={messages.select_brand}
                        name="brand"
                        onChange={this.addBrand}
                        disabled={disabled}
                        autocompleteType="brand"
                        returnValueObject
                      />

                      {!stateBrands.length && (
                        <div className="form-group form-group-default padding-10">
                          <Alert className="no-margin">{messages.all_brands_visible}</Alert>
                        </div>
                      )}

                      {stateBrands.map(brand => {
                        return (
                          <div key={brand.id} className="form-group form-group-default">
                            <Clearfix>
                              <div className="pull-left">{brand.name}</div>
                              {isAdmin && (
                                <div className="pull-right">
                                  <Button
                                    className="text-danger pull-right"
                                    bsSize="xsmall"
                                    onClick={() => this.removeBrand(brand.id)}
                                    disabled={disabled}
                                  >
                                    <i className="fa fa-times" />
                                  </Button>
                                </div>
                              )}
                            </Clearfix>
                          </div>
                        );
                      })}
                    </div>

                    <p className="m-t-15 form-group-title">{messages.sellDownloadPdf}</p>
                    <div className="form-group-attached">
                      <FormSwitchery
                        name="sellDownloadPdf"
                        checked={entity.getSellDownloadPdf()}
                        value
                        label={messages.sellDownloadPdf}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
              </Col>

              {!inModal && isAdmin ? (
                <Col xs={12} md={inModal ? 12 : 4}>
                  <div className="panel panel-transparent">
                    <div className="panel-body">
                      <div>
                        <p className="form-group-title">
                          <i className="fab fa-facebook" /> <i className="fab fa-google" />{' '}
                          <i className="fab fa-microsoft" /> Social Media Login
                        </p>

                        <div className="form-group-attached">
                          <FormInput
                            label={'Facebook Client Id'}
                            name="facebookLoginClientId"
                            value={entity.getFacebookLoginClientId()}
                            meta={entity.getFacebookLoginClientIdMetaInformation()}
                            validation={entity.getFacebookLoginClientIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />
                          <div className="form-group form-group-default">
                            <label>Facebook App Secret</label>
                            <div style={{ position: 'relative', width: '100%' }}>
                              {this.getSocialMediaLoginIcon('facebookSecret')}
                              <Button
                                className="m-l-5"
                                bsSize="xsmall"
                                onClick={() => this.showEnterSocialMediaSecret('facebookSecret')}
                                disabled={disabled}
                              >
                                {messages.enter_app_secret}
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="form-group-attached m-t-10">
                          <FormInput
                            label={'Google Client Id'}
                            name="googleLoginClientId"
                            value={entity.getGoogleLoginClientId()}
                            meta={entity.getGoogleLoginClientIdMetaInformation()}
                            validation={entity.getGoogleLoginClientIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />
                          {/*<FormInput
                            label={'Google iOS Client Id'}
                            name="googleIOSLoginClientId"
                            value={entity.getGoogleIOSLoginClientId()}
                            meta={entity.getGoogleIOSLoginClientIdMetaInformation()}
                            validation={entity.getGoogleIOSLoginClientIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />*/}
                          <div className="form-group form-group-default">
                            <label>Google App Secret</label>
                            <div style={{ position: 'relative', width: '100%' }}>
                              {this.getSocialMediaLoginIcon('googleSecret')}
                              <Button
                                className="m-l-5"
                                bsSize="xsmall"
                                onClick={() => this.showEnterSocialMediaSecret('googleSecret')}
                                disabled={disabled}
                              >
                                {messages.enter_app_secret}
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div className="form-group-attached m-t-10">
                          <FormInput
                            label="Microsoft Client Id"
                            name="microsoftLoginClientId"
                            value={entity.getMicrosoftLoginClientId()}
                            meta={entity.getMicrosoftLoginClientIdMetaInformation()}
                            validation={entity.getMicrosoftLoginClientIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />
                          <FormInput
                            label="Microsoft Directory (Tenant) Id"
                            name="microsoftLoginDirectoryId"
                            value={entity.getMicrosoftLoginDirectoryId()}
                            meta={entity.getMicrosoftLoginDirectoryIdMetaInformation()}
                            validation={entity.getMicrosoftLoginDirectoryIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />
                          <div className="form-group form-group-default">
                            <label>Microsoft App Secret</label>
                            <div style={{ position: 'relative', width: '100%' }}>
                              {this.getSocialMediaLoginIcon('microsoftSecret')}
                              <Button
                                className="m-l-5"
                                bsSize="xsmall"
                                onClick={() => this.showEnterSocialMediaSecret('microsoftSecret')}
                                disabled={disabled}
                              >
                                {messages.enter_app_secret}
                              </Button>
                            </div>
                          </div>
                        </div>

                        <p className="m-t-15 form-group-title">
                          <i className="fab fa-facebook-messenger" /> Facebook Messenger
                        </p>
                        <div className="form-group-attached">
                          <FormInput
                            label={'Facebook App Id'}
                            name="facebookLoginClientId"
                            value={entity.getFacebookLoginClientId()}
                            meta={entity.getFacebookLoginClientIdMetaInformation()}
                            validation={entity.getFacebookLoginClientIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />
                          <FormInput
                            label={'Facebook Page Id'}
                            name="facebookPageId"
                            value={entity.getFacebookPageId()}
                            meta={entity.getFacebookPageIdMetaInformation()}
                            validation={entity.getFacebookPageIdValidationResult()}
                            onChange={this.updateEntity}
                            disabled={disabled}
                          />
                          <div className="form-group form-group-default">
                            <label>Facebook Page Access Token</label>
                            <div style={{ position: 'relative', width: '100%' }}>
                              {this.getSocialMediaLoginIcon('facebookPageAccessToken')}
                              <Button
                                className="m-l-5"
                                bsSize="xsmall"
                                onClick={() => this.showEnterSocialMediaSecret('facebookPageAccessToken')}
                                disabled={disabled}
                              >
                                {messages.enter_page_access_token}
                              </Button>
                            </div>
                          </div>
                        </div>

                        <SocialMediaLoginSecret
                          show={this.state.showEnterSocialMediaSecret}
                          close={this.closeEnterSocialMediaSecret}
                          socialMediaLoginSecrets={socialMediaLoginSecrets.data.list}
                          type={this.state.socialMediaLoginSecretType}
                          whiteLabelId={entity.getId()}
                        />

                        <p className="m-t-15 form-group-title">{messages.social_media}</p>
                        <div className="form-group-attached">
                          <FormInput
                            label="Facebook"
                            name="facebookName"
                            value={entity.getSocialMedia().getFacebookName()}
                            meta={entity.getSocialMedia().getFacebookNameMetaInformation()}
                            validation={entity.getSocialMedia().getFacebookNameValidationResult()}
                            onChange={this.updateSocialMediaEntity}
                            disabled={disabled}
                          />
                          <FormInput
                            label="Twitter"
                            name="twitterName"
                            value={entity.getSocialMedia().getTwitterName()}
                            meta={entity.getSocialMedia().getTwitterNameMetaInformation()}
                            validation={entity.getSocialMedia().getTwitterNameValidationResult()}
                            onChange={this.updateSocialMediaEntity}
                            disabled={disabled}
                          />
                          <FormInput
                            label="Instagram"
                            name="instagramName"
                            value={entity.getSocialMedia().getInstagramName()}
                            meta={entity.getSocialMedia().getInstagramNameMetaInformation()}
                            validation={entity.getSocialMedia().getInstagramNameValidationResult()}
                            onChange={this.updateSocialMediaEntity}
                            disabled={disabled}
                          />
                          <FormInput
                            label="Youtube"
                            name="youtubeName"
                            value={entity.getSocialMedia().getYoutubeName()}
                            meta={entity.getSocialMedia().getYoutubeNameMetaInformation()}
                            validation={entity.getSocialMedia().getYoutubeNameValidationResult()}
                            onChange={this.updateSocialMediaEntity}
                            disabled={disabled}
                          />
                          <FormInput
                            label="Vimeo"
                            name="vimeoName"
                            value={entity.getSocialMedia().getVimeoName()}
                            meta={entity.getSocialMedia().getVimeoNameMetaInformation()}
                            validation={entity.getSocialMedia().getVimeoNameValidationResult()}
                            onChange={this.updateSocialMediaEntity}
                            disabled={disabled}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              ) : null}

              <Col xs={12} md={inModal ? 12 : 4}>
                <div className="panel panel-transparent">
                  <div className="panel-body">
                    <p className="form-group-title">{messages.user_tracking}</p>
                    <div className="form-group-attached">
                      <FormInput
                        label="Google Analytics"
                        name="googleAnalyticsTrackingId"
                        value={entity.getGoogleAnalyticsTrackingId()}
                        meta={entity.getGoogleAnalyticsTrackingIdMetaInformation()}
                        validation={entity.getGoogleAnalyticsTrackingIdValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                      <FormInput
                        label="Facebook Pixel"
                        name="facebookPixelTrackingId"
                        value={entity.getFacebookPixelTrackingId()}
                        meta={entity.getFacebookPixelTrackingIdMetaInformation()}
                        validation={entity.getFacebookPixelTrackingIdValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                    </div>

                    <p className="m-t-15 form-group-title">{'Google Tag Manager'}</p>
                    <div className="form-group-attached">
                      <FormInput
                        label="Google Tag Manager Id"
                        name="googleTagManagerId"
                        value={entity.getGoogleTagManagerId()}
                        meta={entity.getGoogleTagManagerIdMetaInformation()}
                        validation={entity.getGoogleTagManagerIdValidationResult()}
                        onChange={this.updateEntity}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {isSuperadmin && (
              <Row className="m-t-30 b-t b-grey p-t-30">
                <Col xs={12} md={inModal ? 12 : 4}>
                  <div className="panel panel-transparent">
                    <div className="panel-body">
                      <p className="m-t-15 form-group-title">{messages.community}</p>
                      <div className="form-group-attached">
                        <FormSwitchery
                          name="isCommunity"
                          checked={entity.getIsCommunity()}
                          value
                          label={messages.is_community}
                          onChange={this.updateEntity}
                          disabled={disabled}
                        />
                        <FormRichEditor
                          label={messages.description}
                          name="communityDescription"
                          value={entity.getCommunityDescription()}
                          meta={entity.getCommunityDescriptionMetaInformation()}
                          validation={entity.getCommunityDescriptionValidationResult()}
                          onChange={this.updateEntity}
                          disabled={disabled}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        )}
      </div>
    );
  }
}
WhiteLabelData.defaultProps = {
  inModal: false,
};

const mapStateToProps = state => {
  return {
    languages: state.list.languages,
    user: state.login.user,
    socialMediaLoginSecrets: state.list.socialMediaLoginSecrets,
    brands: state.list.whiteLabelBrands,
  };
};

export default injectIntl(connect(mapStateToProps)(WhiteLabelData));
